import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { getBookletsQuery } from '../queries/booklet';
import { getBookletQuery } from '../queries/booklet';
import { getCompanyCategoriesQuery } from '../queries/company';
import { Booklet, Company, Coupon, CompanyCategory } from '../types/generated.types';

export interface CouponWithCompany extends Coupon {
  company?: Company;
}

interface BookletCouponsContextType {
  // State
  selectedBookletId: string | null;
  visibleCompanies: Company[];
  selectedCoupons: (CouponWithCompany | null)[];
  companyCoupons: Record<string, CouponWithCompany[]>;
  availableCoupons: CouponWithCompany[];
  companyCategories: CompanyCategory[];
  selectedCouponIndex: number;
  setSelectedBookletId: (id: string | null) => void;
  setSelectedCoupons: (coupons: (CouponWithCompany | null)[]) => void;
  setVisibleCompanies: (companies: Company[]) => void;
  setSelectedCouponIndex: (index: number) => void;

  // Data
  selectedBooklet: Booklet | null;
  bookletsData: any;

  // Loading states
  bookletsLoading: boolean;
}

const BookletCouponsContext = createContext<BookletCouponsContextType | undefined>(undefined);

export const BookletCouponsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  // State
  const [selectedBookletId, setSelectedBookletId] = useState<string | null>(null);
  const [visibleCompanies, setVisibleCompanies] = useState<Company[]>([]);
  const [companyCoupons, setCompanyCoupons] = useState<Record<string, CouponWithCompany[]>>({});
  const [selectedCoupons, setSelectedCoupons] = useState<(CouponWithCompany | null)[]>(Array(27).fill(null));
  const [selectedCouponIndex, setSelectedCouponIndex] = useState<number>(-1);

  // Queries
  const { data: bookletsData, loading: bookletsLoading } = useQuery(getBookletsQuery);
  const { data: companyCategoriesData } = useQuery(getCompanyCategoriesQuery);

  // Get booklet data when a booklet is selected
  const { data: bookletData } = useQuery(getBookletQuery, {
    variables: { _id: selectedBookletId },
    skip: !selectedBookletId
  });

  const companyCategories = companyCategoriesData?.categories || [];
  const selectedBooklet = bookletsData?.booklets?.find((b: Booklet) => b._id === selectedBookletId);

  // Set visible companies from booklet data when it changes
  useEffect(() => {
    if (bookletData?.booklet?.calculatedCompanies) {
      setVisibleCompanies(bookletData.booklet.calculatedCompanies);
    } else {
      setVisibleCompanies([]);
    }
  }, [bookletData, selectedBookletId]);

  // Set company coupons from calculatedCompanies when visibleCompanies changes
  useEffect(() => {
    if (visibleCompanies.length === 0) {
      setCompanyCoupons({});
      return;
    }

    // Use calculatedCoupons from booklet data
    const newCompanyCoupons = visibleCompanies.reduce((acc, company) => {
      if (company.calculatedCoupons) {
        acc[company._id] = company.calculatedCoupons.map(coupon => ({ ...coupon, company }));
      }
      return acc;
    }, {});

    setCompanyCoupons(newCompanyCoupons);
  }, [visibleCompanies]);

  // Create available coupons list from all companies' coupons
  const availableCoupons = useMemo(() => {
    if (!selectedBookletId) return [];

    return visibleCompanies.flatMap(company =>
      (companyCoupons[company._id] || [])
        .filter(coupon => !selectedCoupons.some(selectedCoupon => selectedCoupon?._id === coupon._id))
    );
  }, [visibleCompanies, companyCoupons, selectedCoupons, selectedBookletId]);

  // Reset coupons when booklet changes
  useEffect(() => {
    if (selectedBookletId) {
      setSelectedCoupons(Array(27).fill(null));
      setCompanyCoupons({});
    }
  }, [selectedBookletId]);

  const value = {
    // State
    selectedBookletId,
    visibleCompanies,
    selectedCoupons,
    companyCoupons,
    availableCoupons,
    companyCategories,
    selectedCouponIndex,
    setSelectedBookletId,
    setSelectedCoupons,
    setVisibleCompanies,
    setSelectedCouponIndex,

    // Data
    selectedBooklet,
    bookletsData,

    // Loading states
    bookletsLoading,
  };

  return (
    <BookletCouponsContext.Provider value={value}>
      {children}
    </BookletCouponsContext.Provider>
  );
};

export const useBookletCoupons = () => {
  const context = useContext(BookletCouponsContext);
  if (context === undefined) {
    throw new Error('useBookletCoupons must be used within a BookletCouponsProvider');
  }
  return context;
};
